import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { auth } from '../../services/firebase';
import userActions from '../../actions/userActions';

declare global {
  interface Window {
    __klikkieUploadUserIdentified?: boolean;
  }
}

type UserData = {
  user_id: string;
  external_id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  country?: string;
};

const pushUserIdentified = (userData: UserData): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: 'user_identified',
      user_data: userData,
    },
  });
};

const buildAndPushIdentified = async (
  user: firebase.default.User,
): Promise<void> => {
  if (typeof window === 'undefined') return;
  if (window.__klikkieUploadUserIdentified) return;
  window.__klikkieUploadUserIdentified = true;

  const userData: UserData = {
    user_id: user.uid,
    external_id: user.uid,
  };

  if (!user.isAnonymous) {
    try {
      const profileUser = await userActions.getUser(user.uid);
      const profile = profileUser?.profile;
      if (profile) {
        if (profile.email) userData.email = profile.email;
        if (profile.name) userData.first_name = profile.name;
        if (profile.surname) userData.last_name = profile.surname;
        if (profile.phonenumber) userData.phone = profile.phonenumber;
        if (profile.countryCode) userData.country = profile.countryCode;
      }
    } catch {
      // Enrichment is best-effort — fall through with the UID-only payload.
    }
  }

  pushUserIdentified(userData);
};

/**
 * Automatically authenticates a user when a ?token=<CUSTOM_TOKEN> query
 * parameter is present. After a successful sign-in the sensitive query
 * parameters are stripped from the URL so that refreshes do not re-trigger
 * the login flow or leak the token.
 *
 * This component must be rendered at the top level of _app.tsx so the effect
 * runs as early as possible on every page navigation.
 */
const AutoLoginInitializer: React.FC = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined; // SSR safeguard

    // Push `user_identified` to GTM dataLayer as soon as Firebase resolves the
    // user (anonymous, cached, or custom-token). The shared GTM container
    // (GTM-N745VV5) uses this event to fire the Meta Pixel Pageview tag with
    // Advanced Matching (External ID = Firebase UID) for EMQ.
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user?.uid) void buildAndPushIdentified(user);
    });

    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');

    // If a token is present and the user is not yet signed in, attempt sign-in.
    if (token && !auth.currentUser) {
      auth
        .signInWithCustomToken(token)
        .catch((error) => {
          // Invalid/expired token – fall back to anonymous auth so the app stays functional
          // You can remove this if your app shouldn't allow anonymous usage.
          console.warn('[AutoLogin] Custom token sign-in failed:', error?.code || error);
          return auth.signInAnonymously().catch(() => undefined);
        })
        .finally(() => {
          // Always clean sensitive query params – whether sign-in succeeded or failed.
          ['token', 'uid', 'id'].forEach((p) => url.searchParams.delete(p));
          window.history.replaceState({}, '', url.toString());
        });
    }

    return unsubscribe;
  }, []);

  return null;
};

export default AutoLoginInitializer; 